// src/components/onboarding/ConsentsPage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import {
  getConsents,
  submitConsents,
} from "../../shared/_services/onboarding.services";
import StarsIcon from "../../styles/StarsIcon";
import { Button, Checkbox, Panel, cn } from "../ui";
import Loader from "../Loader";
import OnboardingLayout from "./OnboardingLayout";
import { useConsentSwitches } from "./ConsentsCard";
import {
  CONSENTS_CONTINUE,
  CONSENTS_H1,
  CONSENTS_HELPER_DONE,
  CONSENTS_INTRO,
  CONSENTS_REQUIRED_GENERIC,
  CONSENTS_REQUIRED_HELPER,
  CONSENTS_REQUIRED_MARK,
  CONSENTS_REQUIRED_TOAST,
  CONSENTS_SIDE_TITLE,
  CONSENTS_WHY,
  GENERIC_ERROR,
  LOAD_FAILED,
  SETTINGS_WITHDRAW,
  TRY_AGAIN,
} from "./onboardingCopy";
import { documentsRailFor, railLabelFor } from "./onboardingFormat";
import { isWrongStage, useStageNavigation } from "./onboardingNav";

// Keyed lines for a required row (decision 13: the row's `required` flag
// drives them; the copy module is keyed by kind, with a generic fallback for
// a required kind published later).
const requiredHelper = (row) =>
  CONSENTS_REQUIRED_HELPER[row.kind] ??
  CONSENTS_REQUIRED_GENERIC(row.shortLabel);
const requiredToast = (row) =>
  CONSENTS_REQUIRED_TOAST[row.kind] ??
  CONSENTS_REQUIRED_GENERIC(row.shortLabel);

function ConsentRow({ row, checked, disabled, onChange, locked }) {
  const [open, setOpen] = useState(false);
  const boxId = `onb-consent-box-${row.kind}`;
  const whyId = `onb-consent-why-${row.kind}`;
  return (
    <Panel
      as="div"
      data-slot="onb-consent"
      data-kind={row.kind}
      className="tw-p-4"
    >
      <div
        data-slot="onb-consent-head"
        className="tw-flex tw-min-h-11 tw-items-center tw-gap-3"
      >
        <Checkbox
          id={boxId}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          labelClassName="tw-flex-1 tw-items-center"
          label={
            <>
              {row.label}
              {row.required && <b>{` ${CONSENTS_REQUIRED_MARK}`}</b>}
            </>
          }
        />
        <Button
          variant="icon"
          iconOnly
          size="sm"
          className="tw-border-0"
          aria-label={CONSENTS_WHY}
          aria-expanded={open}
          aria-controls={whyId}
          onClick={() => setOpen(!open)}
        >
          <StarsIcon
            name="chevron"
            data-slot="onb-chevron"
            className={cn(
              "tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-accent",
              open && "tw-rotate-90",
            )}
          />
        </Button>
      </div>
      <div
        id={whyId}
        data-slot="onb-consent-why"
        className="tw-mt-2 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-pt-2 tw-text-sm tw-text-text-muted"
        hidden={!open}
      >
        {row.why}
      </div>
      {locked && (
        <p
          data-slot="onb-muted"
          className="tw-m-0 tw-mt-2 tw-text-sm tw-text-text-muted"
        >
          {SETTINGS_WITHDRAW}
        </p>
      )}
    </Panel>
  );
}

// /onboarding/consents — "How we work with you" (spec § Consents). After the
// client has submitted, the page is Settings › Consents: switchable rows write
// POST /consents at once, required rows are locked with the withdraw line.
export default function ConsentsPage({
  readOnly = false,
  stage,
  resumed = false,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const go = useStageNavigation();
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [choices, setChoices] = useState({});
  const [sending, setSending] = useState(false);
  const { busyKind, toggle, plaidDialog } = useConsentSwitches(setRows);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await getConsents();
      setRows(res.data.consents || []);
      setChoices({});
    } catch (_) {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const firstMissing = (rows || []).find((r) => r.required && !choices[r.kind]);
  const focusRow = (row) => {
    const box = listRef.current?.querySelector(`#onb-consent-box-${row.kind}`);
    if (!box) return;
    box.closest('[data-slot="onb-consent"]').scrollIntoView({ block: "center" });
    box.focus();
  };

  const submit = async () => {
    if (readOnly) {
      go(stage);
      return;
    }
    if (firstMissing) {
      enqueueSnackbar(requiredToast(firstMissing), { variant: "info" });
      focusRow(firstMissing);
      return;
    }
    setSending(true);
    try {
      const map = Object.fromEntries(
        rows.map((r) => [r.kind, Boolean(choices[r.kind])]),
      );
      const res = await submitConsents(map);
      go(res.data);
    } catch (err) {
      const data = err?.response?.data;
      if (isWrongStage(err)) {
        go(data);
        return;
      }
      if (data?.code === "unknown_kind" || data?.code === "missing_kind") {
        await load();
      } else if (data?.code === "required_consent") {
        enqueueSnackbar(data.message || GENERIC_ERROR, { variant: "error" });
        const row = rows.find((r) => r.kind === data.kind);
        if (row) focusRow(row);
      } else {
        enqueueSnackbar(data?.message || GENERIC_ERROR, { variant: "error" });
      }
      setSending(false);
    }
  };

  const locked = !readOnly && Boolean(firstMissing);
  const side = rows && (
    <>
      {!readOnly && (
        <p
          data-slot="onb-muted"
          className="tw-m-0 tw-text-sm tw-text-text-muted"
        >
          {firstMissing ? requiredHelper(firstMissing) : CONSENTS_HELPER_DONE}
        </p>
      )}
      <Button
        aria-disabled={locked || undefined}
        disabled={sending}
        onClick={submit}
      >
        {CONSENTS_CONTINUE}
        <StarsIcon name="arrow" className="tw-h-4 tw-w-4 tw-shrink-0" />
      </Button>
    </>
  );

  return (
    <OnboardingLayout
      railLabel={railLabelFor(
        readOnly
          ? documentsRailFor("documents_consents", stage?.stages)
          : stage?.rail,
      )}
      sideTitle={CONSENTS_SIDE_TITLE}
      side={side}
      resumed={resumed}
    >
      <h1
        data-slot="onb-h1"
        className="tw-m-0 tw-text-2xl tw-font-bold tw-text-text max-rail:tw-text-xl"
      >
        {CONSENTS_H1}
      </h1>
      <p data-slot="onb-muted" className="tw-m-0 tw-text-md tw-text-text-muted">
        {CONSENTS_INTRO}
      </p>
      {failed && (
        <div role="alert" className="tw-text-sm tw-text-text">
          <span>{LOAD_FAILED}</span>{" "}
          <Button variant="text" size="sm" onClick={load}>
            {TRY_AGAIN}
          </Button>
        </div>
      )}
      {!failed && !rows && <Loader />}
      {rows && (
        <div
          data-slot="onb-consents"
          className="tw-flex tw-flex-col tw-gap-3"
          ref={listRef}
        >
          {rows.map((row) =>
            readOnly ? (
              <ConsentRow
                key={row.kind}
                row={row}
                checked={row.state === true}
                disabled={row.required || busyKind === row.kind}
                locked={row.required}
                onChange={() => toggle(row)}
              />
            ) : (
              <ConsentRow
                key={row.kind}
                row={row}
                checked={Boolean(choices[row.kind])}
                disabled={false}
                locked={false}
                onChange={(e) =>
                  setChoices((c) => ({ ...c, [row.kind]: e.target.checked }))
                }
              />
            ),
          )}
        </div>
      )}
      {plaidDialog}
    </OnboardingLayout>
  );
}
